import { BASE_URL } from '../constants';
import { apiFetch } from '../ApiFetch';
import { getUserSessionCookie } from '../../utils/SessionCookie';

export interface HubspotCompany {
  id: string;
  properties: {
    name?: string;
    domain?: string;
    website?: string;
    city?: string;
    state?: string;
    country?: string;
    industry?: string;
    numberofemployees?: number;
    linkedin_company_page?: string;
    [key: string]: any; // Allow additional HubSpot properties
  };
  createdAt?: string;
  updatedAt?: string;
  archived?: boolean;
  url?: string;
}

export interface HubspotContact {
  id: string;
  properties: {
    firstname?: string;
    lastname?: string;
    email?: string;
    phone?: string;
    hs_linkedin_url?: string;
    associatedcompanyid?: string;
    [key: string]: any; // Allow additional HubSpot properties
  };
  createdAt?: string;
  updatedAt?: string;
  archived?: boolean;
  url?: string;
}

export interface HubspotListResponse<T> {
  records: T[];
  offsetId?: string | null;
}

// Fetch specific HubSpot records by ids
export async function fetchHubspotContactsByIds(args: {
  contactIds: string[];
  properties?: string[];
}): Promise<HubspotContact[]> {
  const sessionCookie = getUserSessionCookie();
  const params = new URLSearchParams();
  args.contactIds.forEach((id) => params.append('contact_ids', id));
  if (args.properties?.length) {
    params.set('properties', args.properties.join(','));
  }

  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/contacts?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok)
    throw new Error(`Fetch HubSpot contacts by ids failed: ${res.status}`);

  const json = await res.json();
  return json.results || [];
}

export async function fetchHubspotCompaniesByIds(args: {
  companyIds: string[];
  properties?: string[];
}): Promise<HubspotCompany[]> {
  const sessionCookie = getUserSessionCookie();
  const params = new URLSearchParams();
  args.companyIds.forEach((id) => params.append('company_ids', id));
  if (args.properties?.length) {
    params.set('properties', args.properties.join(','));
  }

  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/companies?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok)
    throw new Error(`Fetch HubSpot companies by ids failed: ${res.status}`);

  const json = await res.json();
  return json.results || [];
}

export const listHubspotCompanies = async (args: {
  limit?: number;
  offsetId?: string | null;
  search?: string | null;
  filter_by_my_owner?: boolean;
}): Promise<HubspotListResponse<HubspotCompany>> => {
  const sessionCookie = getUserSessionCookie();
  const params = new URLSearchParams();
  if (args.limit) params.set('limit', String(args.limit));
  if (args.offsetId) params.set('after', args.offsetId);
  if (args.search) params.set('search', args.search);
  if (args.filter_by_my_owner !== undefined)
    params.set('filter_by_my_owner', String(args.filter_by_my_owner));

  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/companies/list?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) throw new Error(`List HubSpot companies failed: ${res.status}`);

  const json = await res.json();
  // Handle HubSpot API response structure: { results: [...], paging?: {...}, next_url?: string }
  const results = json.results || json.records || [];
  // Prefer backend-provided next_url, fallback to constructing from paging if needed
  const offsetId = json.paging?.next?.after || null;

  return {
    records: results,
    offsetId: offsetId
  };
};

export const listHubspotContacts = async (args: {
  limit?: number;
  offsetId?: string | null;
  search?: string | null;
}): Promise<HubspotListResponse<HubspotContact>> => {
  const sessionCookie = getUserSessionCookie();
  const params = new URLSearchParams();
  if (args.limit) params.set('limit', String(args.limit));
  if (args.offsetId) params.set('after', args.offsetId);
  if (args.search) params.set('search', args.search);

  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/contacts/list?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) throw new Error(`List HubSpot contacts failed: ${res.status}`);

  const json = await res.json();
  // Handle HubSpot API response structure: { results: [...], paging?: {...}, next_url?: string }
  const results = json.results || json.records || [];
  // Prefer backend-provided next_url, fallback to constructing from paging if needed
  const offsetId = json.paging?.next?.after || null;

  return {
    records: results,
    offsetId: offsetId
  };
};

export interface HubspotSegment {
  listId: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  filtersUpdatedAt?: string;
  processingStatus?: string;
  processingType?: string;
  objectTypeId?: string;
  listVersion?: number;
  additionalProperties?: {
    hs_list_reference_count?: string;
    hs_list_size?: string;
    [key: string]: any;
  };
}

export interface HubspotSegmentListResponse {
  records: HubspotSegment[];
  offset?: number;
  hasMore: boolean;
  total?: number;
}

export const listHubspotSegments = async (args: {
  query?: string;
  offset?: number;
}): Promise<HubspotSegmentListResponse> => {
  const sessionCookie = getUserSessionCookie();

  const res = await apiFetch(`${BASE_URL}/connector/hubspot/segments/search`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify({
      query: args.query || '',
      offset: args.offset || null
    })
  });
  if (!res.ok) throw new Error(`List HubSpot segments failed: ${res.status}`);

  const json = await res.json();
  const records = json.lists || [];
  const offset = json.offset || 0;
  const hasMore = json.hasMore || false;
  const total = json.total || 0;

  return {
    records,
    offset,
    hasMore,
    total
  };
};

export const getHubspotSegmentMembers = async (args: {
  segmentId: string;
  limit?: number;
}): Promise<any[]> => {
  const sessionCookie = getUserSessionCookie();

  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/segments/membership/${args.segmentId}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok)
    throw new Error(`Get HubSpot segment members failed: ${res.status}`);

  const json = await res.json();
  return json;
};

// HubSpot Sync API Functions
import {
  MappingConfig,
  HubSpotFieldForMapping,
  HubSpotTargetObjectName,
  HubSpotSearchResponse,
  UserConfirmedSelections,
  UserConfirmedAssociations,
  SyncJobStatus,
  SyncResult,
  UpsertMappedPreviewResponse,
  ValidationSummary
} from '../../types/hubspot';

export async function getHubSpotSchemaForMapping(
  object: HubSpotTargetObjectName
): Promise<{
  object: string;
  fields: HubSpotFieldForMapping[];
}> {
  const sessionCookie = getUserSessionCookie();
  // Normalize object name to API format
  const objectId =
    object === HubSpotTargetObjectName.CONTACT
      ? HubSpotTargetObjectName.CONTACT
      : HubSpotTargetObjectName.COMPANY;

  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/schema/${objectId}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) throw new Error(`Get schema failed: ${res.status}`);
  return res.json();
}

/**
 * Start a background search job for HubSpot records.
 * Returns job_id for polling status/result.
 *
 * Dedupe search by one or more HubSpot properties (AND).
 * Pass `primary_key_properties` (HubSpot API names, sheet column order) and
 * `search_value_rows` (deduped rows of cell strings aligned to those properties).
 */
export async function startHubSpotSearchJob(args: {
  object: HubSpotTargetObjectName;
  primary_key_properties: string[];
  /** Each inner array is one composite key, aligned with `primary_key_properties`. */
  search_value_rows: string[][];
  normalize?: 'website' | 'linkedin' | null;
}): Promise<{
  job_id: string;
  status: string;
  created_at: string;
  queue_position?: number;
}> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(`${BASE_URL}/connector/hubspot/search`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify(args)
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Start search job failed: ${res.status} - ${errorText}`);
  }
  return res.json();
}

export async function previewHubSpotSync(args: {
  mapping: MappingConfig;
  rows: Record<string, any>[];
  user_confirmed_selections?: UserConfirmedSelections;
  user_confirmed_associations?: UserConfirmedAssociations;
}): Promise<{
  job_id: string;
  status: string;
  created_at: string;
  queue_position?: number;
}> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(`${BASE_URL}/connector/hubspot/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify(args)
  });
  if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
  return res.json();
}

export async function startHubSpotSync(args: {
  mapping: MappingConfig;
  rows: Record<string, any>[];
  user_confirmed_selections?: UserConfirmedSelections;
  user_confirmed_associations?: UserConfirmedAssociations;
}): Promise<{
  job_id: string;
  status: string;
  created_at: string;
  queue_position?: number;
}> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(`${BASE_URL}/connector/hubspot/sync`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify(args)
  });
  if (!res.ok) throw new Error(`Start sync failed: ${res.status}`);
  return res.json();
}

/**
 * Get status for a HubSpot job (works for both preview and sync jobs).
 */
export async function getHubSpotJobStatus(
  jobId: string
): Promise<SyncJobStatus> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/job/status?job_id=${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) throw new Error(`Get status failed: ${res.status}`);
  return res.json();
}

/**
 * Get result for a HubSpot job (works for both preview and sync jobs).
 * For preview jobs, returns UpsertMappedPreviewResponse.
 * For sync jobs, returns SyncResult.
 */
export async function getHubSpotJobResult(
  jobId: string
): Promise<UpsertMappedPreviewResponse | SyncResult> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/job/result?job_id=${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok) {
    if (res.status === 404) {
      throw new Error('Result not ready yet');
    }
    throw new Error(`Get result failed: ${res.status}`);
  }
  return res.json();
}

/** Request body for HubSpot sync validation (POST /connector/hubspot/validation). */
export interface HubSpotValidationRequest {
  mapping: MappingConfig;
  rows: Record<string, any>[];
}

/** Response from HubSpot validation endpoint. */
export interface HubSpotValidationResponse {
  validation_summary: ValidationSummary;
}

/**
 * Run HubSpot CRM sync validation for mapped fields and rows.
 * Returns per-category, per-field valid/invalid counts (same shape as Salesforce-style summary).
 */
export async function validateHubSpotMapping(
  request: HubSpotValidationRequest
): Promise<HubSpotValidationResponse> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(`${BASE_URL}/connector/hubspot/validation`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify(request)
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`HubSpot validation failed: ${res.status} - ${errorText}`);
  }
  return res.json();
}

// Fetch HubSpot object properties
export async function fetchContactObjectProperties(): Promise<{
  properties: Record<string, string>;
}> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/contacts/properties`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok)
    throw new Error(`Fetch contact object properties failed: ${res.status}`);

  return res.json();
}

export async function fetchCompanyObjectProperties(): Promise<{
  properties: Record<string, string>;
}> {
  const sessionCookie = getUserSessionCookie();
  const res = await apiFetch(
    `${BASE_URL}/connector/hubspot/companies/properties`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
      }
    }
  );
  if (!res.ok)
    throw new Error(`Fetch company object properties failed: ${res.status}`);

  return res.json();
}
